import { action } from "mobx";
import EshopError from "models/eshopError";
import ISDSRepository from "repositories/isdsRepository";
import { Notice } from "services/NIA/niaService";
import { persistCallbackDestination } from "utils/externalServiceUtils";
import { ISDSAtsId, ISDSUrl } from "../../config";
import EventBus, {
  exemptionISDSConceptStatusCheckFailure,
  exemptionISDSConceptStatusCheckSuccess,
  licensePlateChangeISDSConceptStatusCheckFailure,
  licensePlateChangeISDSConceptStatusCheckSuccess,
  omissionISDSConceptStatusCheckFailure,
  omissionISDSConceptStatusCheckSuccess,
  refundRequestISDSConceptStatusCheckFailure,
  refundRequestISDSConceptStatusCheckSuccess,
} from "../eventBus";
import * as Sentry from "@sentry/react";

export const SESSION_STORAGE_ISDS_REQUEST_SOURCE_KEY = "ISDS_REQUEST_SOURCE";
const ISDS_CALLBACK_DESTINATION_PATH = "notice";
const ISDS_CONCEPT_CHECK_FAILURE = "ISDS_CONCEPT_CHECK_FAILURE";

enum ConceptStatus {
  Success = "0000",
  DeclinedByUser = "2305",
}

export default class ISDSService {
  constructor(private isdsRepository: ISDSRepository, private eventBus: EventBus) {}

  @action
  redirectToISDS(notice: Notice) {
    persistCallbackDestination(`${notice}/${notice}_${ISDS_CALLBACK_DESTINATION_PATH}`);
    sessionStorage.setItem(SESSION_STORAGE_ISDS_REQUEST_SOURCE_KEY, notice);
    window.location.href = this.generateISDSRedirectURL();
  }

  @action
  redirectToISDSConcept(notice: Notice, conceptIdent: string) {
    persistCallbackDestination(`${notice}/${notice}_${ISDS_CALLBACK_DESTINATION_PATH}`);
    window.location.href = this.generateISDSConceptRedirectURL(conceptIdent);
  }

  @action
  async conceptStatus(sessionId: string, notice: Notice) {
    return this.isdsRepository
      .conceptStatus(sessionId)
      .then((response) => {
        if (response instanceof EshopError) {
          this.publishConceptError(notice, response.type, undefined, response);
        } else {
          switch (response.statusCode) {
            case ConceptStatus.Success:
              this.publishConceptSuccess(notice);
              break;
            case ConceptStatus.DeclinedByUser:
              break;
            default:
              this.publishConceptError(notice, ISDS_CONCEPT_CHECK_FAILURE, response.statusCode, response);
              break;
          }
        }
      })
      .catch((error) => {
        this.publishConceptError(notice, ISDS_CONCEPT_CHECK_FAILURE, undefined, error);
      });
  }

  private publishConceptSuccess = (notice: Notice) => {
    switch (notice) {
      case Notice.EXEMPTION:
        this.eventBus.publish(exemptionISDSConceptStatusCheckSuccess({ success: true, notice }));
        break;
      case Notice.OMISSION:
        this.eventBus.publish(omissionISDSConceptStatusCheckSuccess({ success: true, notice }));
        break;
      case Notice.LICENSE_PLATE_CHANGE:
        this.eventBus.publish(licensePlateChangeISDSConceptStatusCheckSuccess({ success: true, notice }));
        break;
      case Notice.REFUND_REQUEST:
        this.eventBus.publish(refundRequestISDSConceptStatusCheckSuccess({ success: true, notice }));
        break;
    }
  };

  private publishConceptError = (notice: Notice, type: string, code: string | undefined, error: any) => {
    Sentry.withScope((scope) => {
      scope.setTag("ISDS", "concept");
      scope.setExtras({
        notice,
        type,
      });
      Sentry.captureException(error);
    });

    switch (notice) {
      case Notice.EXEMPTION:
        this.eventBus.publish(exemptionISDSConceptStatusCheckFailure({ type, code }));
        break;
      case Notice.OMISSION:
        this.eventBus.publish(omissionISDSConceptStatusCheckFailure({ type, code }));
        break;
      case Notice.LICENSE_PLATE_CHANGE:
        this.eventBus.publish(licensePlateChangeISDSConceptStatusCheckFailure({ type, code }));
        break;
      case Notice.REFUND_REQUEST:
        this.eventBus.publish(refundRequestISDSConceptStatusCheckFailure({ type, code }));
        break;
    }
  };

  private generateISDSRedirectURL = () => {
    const redirect = new URL("as/login", ISDSUrl);
    redirect.searchParams.append("atsId", ISDSAtsId!);

    return redirect.toString();
  };

  private generateISDSConceptRedirectURL = (conceptIdent: string) => {
    const redirect = new URL("as/koncept/view", ISDSUrl);
    redirect.searchParams.append("konceptId", conceptIdent);

    return redirect.toString();
  };
}
